import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// GA4 tracking helper - safe to call even if gtag isn't loaded
const trackEvent = (eventName, params = {}) => {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('event', eventName, params);
  }
};

const AdLandingPage = () => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Capture UTM parameters on landing for attribution through to signup.
  // Stored in sessionStorage (last-touch attribution, tab-scoped) so they
  // survive navigation from /organizer -> /register and make it into the
  // registration POST body. The RegisterPage reads these values back.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const utmData = {
      utm_source:   params.get('utm_source')   || null,
      utm_medium:   params.get('utm_medium')   || null,
      utm_campaign: params.get('utm_campaign') || null,
      utm_content:  params.get('utm_content')  || null,
      landing_page: window.location.pathname,
    };
    // Only overwrite sessionStorage if this visit actually has UTM data,
    // or if nothing's stored yet. This preserves attribution if the user
    // opens /organizer, then visits another page, then returns without UTMs.
    const hasAnyUtm = utmData.utm_source || utmData.utm_medium ||
                      utmData.utm_campaign || utmData.utm_content;
    const existing = sessionStorage.getItem('tto_attribution');
    if (hasAnyUtm || !existing) {
      sessionStorage.setItem('tto_attribution', JSON.stringify(utmData));
    }
  }, []);

  // Minimal SEO setup - this page is noindex (paid/social traffic only)
  useEffect(() => {
    document.title = 'Tee Time Organizer | For Golf Group Organizers';

    // noindex so it doesn't compete with the main landing page in Google
    let metaRobots = document.querySelector('meta[name="robots"]');
    if (!metaRobots) {
      metaRobots = document.createElement('meta');
      metaRobots.name = 'robots';
      document.head.appendChild(metaRobots);
    }
    metaRobots.content = 'noindex, nofollow';

    // Basic description (for link previews if someone shares it)
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = 'Stop chasing guys for RSVPs. Tee Time Organizer runs your golf group for you. 30 days free, cancel anytime.';

    return () => {
      // Clean up noindex when navigating away so main landing page SEO isn't affected
      const robots = document.querySelector('meta[name="robots"]');
      if (robots) robots.content = 'index, follow';
    };
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Colors - matching main landing page
  const colors = {
    green900: '#14532d', green800: '#166534', green700: '#15803d', green600: '#16a34a',
    green500: '#22c55e', green100: '#dcfce7', green50: '#f0fdf4',
    gray900: '#111827', gray800: '#1f2937', gray700: '#374151', gray600: '#4b5563',
    gray500: '#6b7280', gray400: '#9ca3af', gray300: '#d1d5db', gray200: '#e5e7eb',
    gray100: '#f3f4f6', gray50: '#f9fafb',
    amber500: '#f59e0b', amber400: '#fbbf24',
    red600: '#dc2626', red50: '#fef2f2',
  };

  // The pain list — the hero emotional hook
  const painPoints = [
    { icon: '📧', pain: 'Reply-all email chains with everyone CC\'d', sub: 'Your inbox is a mess. Theirs is too.' },
    { icon: '❓', pain: 'Chasing guys all week to confirm the next round', sub: '"You in this week?" — asked one text at a time.' },
    { icon: '⏰', pain: 'The 7 AM Saturday cancellation', sub: 'Now you\'re scrambling, or the pro shop is short a player.' },
    { icon: '💸', pain: 'Empty seats at the tee — or too many guys showing up', sub: 'You book 4 tee times hoping 16 show. Instead 11 confirm, 2 ghost. Now you\'re eating fees or apologizing to the pro shop.' },
    { icon: '📋', pain: 'Friday-night pro shop email with the scraped-together list', sub: 'Copy-paste from texts. Miss a guy. Send a correction. Look like you don\'t have it together.' },
    { icon: '🎯', pain: 'Figuring out who plays with who', sub: 'Balanced flights by handicap? Random? Same-pairing complaints from the same two guys? It\'s a weekly puzzle.' },
  ];

  // What the app actually does — the relief
  const reliefPoints = [
    { title: 'RSVPs that happen without you', desc: 'One-click email link. Players confirm in seconds. You see who\'s in on a live tee sheet — no more counting text replies.' },
    { title: 'Automated reminders the night before and morning of', desc: 'Email and SMS reminders with weather and the full details. The "I forgot" excuse is gone.' },
    { title: 'Waitlist auto-promotes when someone cancels', desc: 'Guy drops out at 7 AM? The next guy on the waitlist gets notified automatically. You keep sleeping.' },
    { title: 'Pairings done for you — balanced, random, or manual', desc: 'Generate balanced flights by course handicap, randomize, or drag-and-drop to arrange. Course handicaps calculated automatically using the official WHS formula.' },
    { title: 'Tee sheet ready to send to the pro shop in one click', desc: 'No more scraping text threads on Friday night. Your confirmed list prints or emails straight to the pro shop, clean and accurate.' },
    { title: 'A mobile portal in every player\'s pocket', desc: 'Players see their tee times, weather, pairings, and tee sheet on their phone. They RSVP, check-in, and enter scores — all from one link. No App Store, no passwords, no downloads.' },
  ];

  // Trust signals for below the fold
  const trustSignals = [
    { num: '30', label: 'Days free — no charge until day 31' },
    { num: '< 30 sec', label: 'To publish a tee time to your group' },
    { num: '$9.99', suffix: '/mo', label: 'Less than a sleeve of golf balls' },
  ];

  return (
    <div style={{ fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif" }}>

      {/* Minimal Navigation - just logo and login */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        background: 'rgba(255, 255, 255, 0.97)', backdropFilter: 'blur(12px)',
        borderBottom: `1px solid ${colors.gray200}`, padding: isMobile ? '10px 16px' : '12px 32px',
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '10px' : '14px' }}>
            <div style={{
              width: isMobile ? '36px' : '44px', height: isMobile ? '36px' : '44px',
              background: 'white', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center',
              overflow: 'hidden', border: `1px solid ${colors.gray200}`,
            }}>
              <img src="/logo.png" alt="Tee Time Organizer" width={isMobile ? 28 : 36} height={isMobile ? 28 : 36} style={{ objectFit: 'contain' }} />
            </div>
            <div>
              <div style={{ fontSize: isMobile ? '14px' : '16px', fontWeight: 700, color: colors.gray900 }}>Tee Time Organizer</div>
              {!isMobile && <div style={{ fontSize: '12px', color: colors.gray500, fontWeight: 500 }}>Built for the guy running the group</div>}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button onClick={() => navigate('/login')} style={{
              padding: isMobile ? '8px 12px' : '12px 24px', fontSize: isMobile ? '12px' : '14px', fontWeight: 600, borderRadius: '10px', border: 'none', cursor: 'pointer',
              background: `linear-gradient(135deg, ${colors.green700} 0%, ${colors.green900} 100%)`, color: 'white',
              boxShadow: '0 2px 8px rgba(22, 101, 52, 0.3)',
            }}>{isMobile ? 'Login' : 'Organizer Login'}</button>
          </div>
        </div>
      </nav>

      {/* Hero Section - The Pain Hook */}
      <section style={{ padding: isMobile ? '100px 20px 40px' : '130px 32px 60px', background: 'white', position: 'relative' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>

          {/* Pre-headline / context badge */}
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '8px',
            background: colors.green100, color: colors.green800,
            padding: '8px 16px', borderRadius: '50px', fontSize: '13px', fontWeight: 600, marginBottom: '24px',
            border: '1px solid rgba(22, 101, 52, 0.1)',
          }}>⛳ For the guy who runs the group</div>

          {/* The headline — pain first */}
          <h1 style={{
            fontFamily: "'DM Serif Display', Georgia, serif",
            fontSize: isMobile ? '34px' : '54px', fontWeight: 400, color: colors.gray900,
            lineHeight: 1.1, marginBottom: '20px', letterSpacing: '-1px',
          }}>
            Still chasing the guys<br />
            <span style={{ color: colors.green700 }}>for Saturday's tee times?</span>
          </h1>

          {/* Subhead — pivot to empathy + solution */}
          <p style={{
            fontSize: isMobile ? '16px' : '20px', color: colors.gray600, lineHeight: 1.6,
            marginBottom: '32px', maxWidth: '680px', margin: '0 auto 32px',
          }}>
            The reply-all chain. The "who's in?" texts. The 7 AM Saturday cancellation.
            You volunteered to organize — you didn't sign up to be a secretary.
            <strong style={{ color: colors.gray900 }}> We built the app that does it for you.</strong>
          </p>

          {/* Primary CTA */}
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '16px' }}>
            <button onClick={() => { trackEvent('ad_landing_cta_click', { location: 'hero' }); trackEvent('signup_cta_click', { location: 'ad_hero' }); navigate('/register'); }} style={{
              padding: isMobile ? '14px 28px' : '16px 36px', fontSize: isMobile ? '15px' : '17px', fontWeight: 700, borderRadius: '12px', border: 'none', cursor: 'pointer',
              background: `linear-gradient(135deg, ${colors.green700} 0%, ${colors.green900} 100%)`, color: 'white',
              boxShadow: '0 4px 16px rgba(22, 101, 52, 0.35)', display: 'inline-flex', alignItems: 'center', gap: '8px',
            }}>Try It Free for 30 Days</button>
          </div>

          {/* Risk-reversal line — prominent, not gray400 */}
          <p style={{ fontSize: isMobile ? '14px' : '15px', color: colors.gray700, fontWeight: 500, marginTop: '8px', marginBottom: '0' }}>
            <span style={{ color: colors.green700, fontWeight: 700 }}>✓</span> No charge until day 31
            &nbsp;·&nbsp; <span style={{ color: colors.green700, fontWeight: 700 }}>✓</span> Cancel anytime
            &nbsp;·&nbsp; <span style={{ color: colors.green700, fontWeight: 700 }}>✓</span> Works on every player's phone
          </p>
        </div>
      </section>

      {/* Pain Section — "You know this list" */}
      <section style={{ padding: isMobile ? '40px 20px' : '60px 32px', background: colors.gray50 }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <header style={{ textAlign: 'center', marginBottom: isMobile ? '32px' : '48px' }}>
            <div style={{
              display: 'inline-block', background: colors.red50, color: colors.red600,
              padding: '6px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '16px',
            }}>If you run a golf group, you know this list</div>
            <h2 style={{
              fontFamily: "'DM Serif Display', Georgia, serif",
              fontSize: isMobile ? '28px' : '38px', color: colors.gray900,
              marginBottom: '12px', letterSpacing: '-0.5px',
            }}>
              Every week, it's the same thing.
            </h2>
            <p style={{ fontSize: isMobile ? '15px' : '17px', color: colors.gray600, lineHeight: 1.6 }}>
              You're not alone. Every organizer we talk to is living some version of this.
            </p>
          </header>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
            gap: isMobile ? '12px' : '16px',
          }}>
            {painPoints.map((item, i) => (
              <div key={i} style={{
                background: 'white',
                borderRadius: '14px',
                padding: isMobile ? '18px' : '22px',
                display: 'flex',
                gap: '14px',
                alignItems: 'flex-start',
                border: `1px solid ${colors.gray200}`,
              }}>
                <div style={{ fontSize: '26px', flexShrink: 0, lineHeight: 1 }}>{item.icon}</div>
                <div>
                  <div style={{
                    fontSize: isMobile ? '15px' : '16px',
                    fontWeight: 700,
                    color: colors.gray900,
                    marginBottom: '4px',
                    lineHeight: 1.35,
                  }}>{item.pain}</div>
                  <div style={{
                    fontSize: isMobile ? '13px' : '14px',
                    color: colors.gray500,
                    lineHeight: 1.5,
                  }}>{item.sub}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Transition / Relief Section */}
      <section style={{ padding: isMobile ? '48px 20px' : '72px 32px', background: 'white' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <header style={{ textAlign: 'center', marginBottom: isMobile ? '36px' : '48px' }}>
            <div style={{
              display: 'inline-block', background: colors.green100, color: colors.green800,
              padding: '6px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '16px',
            }}>Here's what changes</div>
            <h2 style={{
              fontFamily: "'DM Serif Display', Georgia, serif",
              fontSize: isMobile ? '28px' : '40px', color: colors.gray900,
              marginBottom: '16px', letterSpacing: '-0.5px',
            }}>
              Imagine your nights and weekends back.
            </h2>
            <p style={{ fontSize: isMobile ? '15px' : '17px', color: colors.gray600, lineHeight: 1.6, maxWidth: '620px', margin: '0 auto' }}>
              Tee Time Organizer handles the stuff you've been doing by hand for years. Here's how.
            </p>
          </header>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: isMobile ? '14px' : '18px' }}>
            {reliefPoints.map((item, i) => (
              <div key={i} style={{
                background: colors.gray50,
                borderLeft: `4px solid ${colors.green600}`,
                borderRadius: '10px',
                padding: isMobile ? '18px 20px' : '22px 26px',
              }}>
                <h3 style={{
                  fontSize: isMobile ? '16px' : '18px',
                  fontWeight: 700,
                  color: colors.gray900,
                  marginBottom: '6px',
                  marginTop: 0,
                }}>✓ {item.title}</h3>
                <p style={{
                  fontSize: isMobile ? '14px' : '15px',
                  color: colors.gray600,
                  lineHeight: 1.6,
                  margin: 0,
                }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Signals / Stats strip */}
      <section style={{
        padding: isMobile ? '36px 20px' : '48px 32px',
        background: `linear-gradient(135deg, ${colors.green50} 0%, white 100%)`,
        borderTop: `1px solid ${colors.gray200}`,
        borderBottom: `1px solid ${colors.gray200}`,
      }}>
        <div style={{
          maxWidth: '900px', margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
          gap: isMobile ? '20px' : '32px',
          textAlign: 'center',
        }}>
          {trustSignals.map((item, i) => (
            <div key={i}>
              <div style={{
                fontSize: isMobile ? '36px' : '48px',
                fontWeight: 800,
                color: colors.green700,
                lineHeight: 1,
                marginBottom: '6px',
                fontFamily: "'DM Serif Display', Georgia, serif",
              }}>
                {item.num}
                {item.suffix && (
                  <span style={{ fontSize: isMobile ? '18px' : '22px', fontWeight: 400 }}>{item.suffix}</span>
                )}
              </div>
              <div style={{ fontSize: isMobile ? '13px' : '14px', color: colors.gray600, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                {item.label}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Price-anchoring + final CTA */}
      <section style={{
        padding: isMobile ? '48px 20px' : '72px 32px',
        background: `linear-gradient(135deg, ${colors.green800} 0%, ${colors.green900} 100%)`,
        textAlign: 'center',
        position: 'relative',
      }}>
        <div style={{ maxWidth: '700px', margin: '0 auto' }}>
          <h2 style={{
            fontFamily: "'DM Serif Display', Georgia, serif",
            fontSize: isMobile ? '30px' : '44px',
            color: 'white',
            marginBottom: '20px',
            letterSpacing: '-0.5px',
            lineHeight: 1.15,
          }}>
            Give it 30 days.<br />If it's not worth it, walk away.
          </h2>
          <p style={{
            fontSize: isMobile ? '15px' : '18px',
            color: 'rgba(255, 255, 255, 0.9)',
            marginBottom: '14px',
            lineHeight: 1.6,
          }}>
            Plans start at <strong style={{ color: 'white' }}>$9.99/month</strong> after the trial. Less than you spend on range balls in a month.
          </p>
          <p style={{
            fontSize: isMobile ? '13px' : '14px',
            color: 'rgba(255, 255, 255, 0.75)',
            marginBottom: '32px',
          }}>
            Credit card required to start. You won't be charged until day 31. Cancel anytime in your dashboard — one click, no phone call, no "are you sure" survey.
          </p>

          <button onClick={() => { trackEvent('ad_landing_cta_click', { location: 'cta_bottom' }); trackEvent('signup_cta_click', { location: 'ad_cta_bottom' }); navigate('/register'); }} style={{
            padding: isMobile ? '16px 32px' : '20px 48px',
            background: 'white',
            color: colors.green900,
            borderRadius: '12px',
            fontSize: isMobile ? '16px' : '18px',
            fontWeight: 700,
            border: 'none',
            cursor: 'pointer',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.25)',
            display: 'inline-flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '4px',
          }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              Start My 30-Day Free Trial <span>⛳</span>
            </span>
            <span style={{ fontSize: '12px', fontWeight: 500, color: colors.gray500 }}>
              No charge until day 31
            </span>
          </button>

          <div style={{ marginTop: '24px' }}>
            <button onClick={() => { trackEvent('demo_cta_click', { location: 'ad_bottom' }); window.location.href = '/dashboard?demo=true&clear=auth'; }} style={{
              padding: '10px 20px',
              background: 'transparent',
              color: 'rgba(255, 255, 255, 0.9)',
              border: '1px solid rgba(255, 255, 255, 0.4)',
              borderRadius: '10px',
              fontSize: '13px',
              fontWeight: 500,
              cursor: 'pointer',
            }}>
              Or poke around the demo first →
            </button>
          </div>
        </div>
      </section>

      {/* Short FAQ — just 3 objection-killers */}
      <section style={{ padding: isMobile ? '40px 20px' : '56px 32px', background: 'white' }}>
        <div style={{ maxWidth: '720px', margin: '0 auto' }}>
          <h2 style={{
            fontFamily: "'DM Serif Display', Georgia, serif",
            fontSize: isMobile ? '24px' : '30px',
            color: colors.gray900,
            marginBottom: '24px',
            textAlign: 'center',
          }}>
            Questions we get a lot
          </h2>

          {[
            { q: 'Do my guys need to download an app or make an account?', a: 'No. They get a personal link by email. One click, they\'re in. Works on any phone, any browser. No passwords, no downloads, no accounts for them to create.' },
            { q: 'What happens if I cancel during the trial?', a: 'Your card is not charged. Period. You cancel from your dashboard — one click. No phone call, no retention pitch, no "wait, let us offer you a discount." You leave, you owe nothing.' },
            { q: 'I\'m the only one who pays, but my guys get all the benefit. Is it worth it?', a: 'Plans start at $9.99/month — that\'s less than a sleeve of good balls. If it saves you one hour of Sunday-night texting per week (about 52 hours a year), it pays for itself many times over. And if it doesn\'t, you cancel.' },
          ].map((faq, i) => (
            <details key={i} style={{
              background: colors.gray50, border: `1px solid ${colors.gray200}`, borderRadius: '12px',
              padding: 0, marginBottom: '12px',
            }}>
              <summary style={{
                padding: isMobile ? '16px 18px' : '18px 22px',
                cursor: 'pointer',
                fontSize: isMobile ? '15px' : '16px',
                fontWeight: 600,
                color: colors.gray900,
                listStyle: 'none',
              }}>{faq.q}</summary>
              <div style={{
                padding: isMobile ? '0 18px 16px' : '0 22px 18px',
                fontSize: isMobile ? '14px' : '15px',
                color: colors.gray600,
                lineHeight: 1.65,
              }}>{faq.a}</div>
            </details>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer style={{ padding: isMobile ? '28px 20px' : '36px 32px', background: colors.gray900 }}>
        <div style={{
          maxWidth: '1200px', margin: '0 auto',
          display: 'flex', flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between', alignItems: 'center', gap: isMobile ? '20px' : '0',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{
              width: '40px', height: '40px', background: 'white', borderRadius: '10px',
              display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
            }}>
              <img src="/logo.png" alt="Tee Time Organizer" width="32" height="32" style={{ objectFit: 'contain' }} />
            </div>
            <div>
              <div style={{ fontSize: '15px', fontWeight: 700, color: 'white' }}>Tee Time Organizer</div>
              <div style={{ fontSize: '12px', color: colors.gray300 }}>© 2025–2026 Tee Time Organizer</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', justifyContent: 'center' }}>
            <span onClick={() => navigate('/privacy')} style={{ color: colors.gray300, fontSize: '13px', cursor: 'pointer' }}>Privacy</span>
            <span onClick={() => navigate('/')} style={{ color: colors.gray300, fontSize: '13px', cursor: 'pointer' }}>Full Site</span>
            <span onClick={() => navigate('/login')} style={{ color: colors.gray300, fontSize: '13px', cursor: 'pointer' }}>Login</span>
          </div>
        </div>
      </footer>

    </div>
  );
};

export default AdLandingPage;
